import type { SessionExport } from '../experiments/EventLogger.ts';
import { Replay } from '../experiments/Replay.ts';
import { button, el, panel, slider } from './dom.ts';

export interface ReplayPanelCallbacks {
  /** The live session's log, so the current run can be replayed with one click. */
  getCurrentExport: () => SessionExport;
  onReplayChanged: (replay: Replay | null) => void;
}

/**
 * Deterministic replay controls.
 *
 * A replay is reconstructed from the seed plus the ticks on which flaps were
 * executed, so a session file is small and a replay is exact. `Verify`
 * re-simulates every run in the file and checks the reconstructed score and
 * death tick against what was recorded — a direct test that the engine is still
 * deterministic, which every comparison in the study quietly depends on.
 *
 * Slow motion is the point of the whole feature: at 0.1x you can watch the
 * four frames between the bot deciding to flap and the participant's hand
 * arriving, and see which of those frames the bird needed it.
 */
export class ReplayPanel {
  readonly root: HTMLElement;

  private replay: Replay | null = null;
  private statusEl: HTMLElement;
  private verifyEl: HTMLElement;
  private playBtn: HTMLButtonElement;
  private scrub: HTMLInputElement;
  private segmentRow: HTMLElement;
  private speedControl: { row: HTMLElement; set: (v: number) => void };

  constructor(private readonly cb: ReplayPanelCallbacks) {
    const p = panel('Replay');
    this.root = p.root;

    const load = el('div', 'button-row wrap');
    load.append(
      button('Replay current session', () => this.loadCurrent(), 'btn-primary'),
      button('Load session file…', () => this.pickFile()),
      button('Exit replay', () => this.close(), 'btn-secondary'),
    );
    p.body.appendChild(load);

    this.statusEl = el('div', 'hint', 'No replay loaded. The live game is showing.');
    p.body.appendChild(this.statusEl);

    this.segmentRow = el('div', 'button-row wrap');
    p.body.appendChild(this.segmentRow);

    const transport = el('div', 'button-row');
    this.playBtn = button('Play', () => this.togglePlay());
    transport.append(
      this.playBtn,
      button('Restart', () => {
        this.replay?.restart();
        this.sync();
      }),
      button('Step', () => {
        this.replay?.step();
        this.sync();
      }),
      button('Verify determinism', () => this.verify(), 'btn-secondary'),
    );
    p.body.appendChild(transport);

    this.speedControl = slider('Playback speed', 1, 0.05, 4, 0.05, (v) => {
      if (this.replay) this.replay.speed = v;
    }, (v) => `${v.toFixed(2)}×`);
    p.body.appendChild(this.speedControl.row);

    const speedChips = el('div', 'button-row wrap');
    for (const s of [0.1, 0.25, 0.5, 1, 2]) {
      speedChips.appendChild(
        button(`${s}×`, () => {
          if (this.replay) this.replay.speed = s;
          this.speedControl.set(s);
        }, 'btn-chip'),
      );
    }
    p.body.appendChild(speedChips);

    this.scrub = el('input');
    this.scrub.type = 'range';
    this.scrub.min = '0';
    this.scrub.max = '1';
    this.scrub.value = '0';
    this.scrub.className = 'scrub';
    this.scrub.addEventListener('input', () => {
      this.replay?.seek(Number(this.scrub.value));
      this.sync(false);
    });
    p.body.appendChild(this.scrub);

    this.verifyEl = el('div', 'verify-results');
    p.body.appendChild(this.verifyEl);
  }

  get current(): Replay | null {
    return this.replay;
  }

  private loadCurrent(): void {
    try {
      this.open(Replay.fromExport(this.cb.getCurrentExport()));
    } catch (err) {
      this.statusEl.textContent = `Could not build a replay: ${String(err)}`;
    }
  }

  private pickFile(): void {
    const input = el('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      void file.text().then((text) => {
        try {
          this.open(Replay.fromExport(text));
        } catch (err) {
          this.statusEl.textContent = `Could not load: ${String(err)}`;
        }
      });
    });
    input.click();
  }

  private open(replay: Replay): void {
    this.replay = replay;
    replay.playing = false;
    this.renderSegments();
    this.sync();
    this.cb.onReplayChanged(replay);
  }

  close(): void {
    this.replay = null;
    this.verifyEl.replaceChildren();
    this.segmentRow.replaceChildren();
    this.statusEl.textContent = 'No replay loaded. The live game is showing.';
    this.cb.onReplayChanged(null);
  }

  private renderSegments(): void {
    this.segmentRow.replaceChildren();
    const replay = this.replay;
    if (!replay) return;
    replay.segments.forEach((seg, i) => {
      const flaps = seg.flapTicks.length;
      const b = button(
        `run ${i + 1} · ${seg.recordedScore ?? 0} pts · ${flaps} flaps`,
        () => {
          replay.selectSegment(i);
          this.sync();
        },
        `btn-chip${i === replay.currentIndex ? ' active' : ''}`,
      );
      this.segmentRow.appendChild(b);
    });
  }

  private togglePlay(): void {
    if (!this.replay) return;
    this.replay.playing = !this.replay.playing;
    this.sync();
  }

  /** Refresh the transport UI. Called every frame while a replay is open. */
  sync(renderSegments = true): void {
    const replay = this.replay;
    if (!replay) return;
    if (renderSegments) this.renderSegments();
    const length = Math.max(1, replay.lengthTicks);
    this.scrub.max = String(length);
    this.scrub.value = String(replay.getState().tick);
    this.playBtn.textContent = replay.playing ? 'Pause' : 'Play';
    const seg = replay.segment;
    this.statusEl.textContent = seg
      ? `Run ${replay.currentIndex + 1}/${replay.segments.length} · seed ${seg.seed} · ` +
        `tick ${replay.getState().tick}/${length} · score ${replay.getState().score} · ` +
        `${replay.speed.toFixed(2)}× speed`
      : 'Empty replay.';
  }

  private verify(): void {
    const replay = this.replay;
    if (!replay) return;
    const results = replay.verify();
    this.verifyEl.replaceChildren();
    for (const r of results) {
      const row = el('div', `verify-row ${r.ok ? 'ok' : 'bad'}`);
      row.textContent =
        `run ${r.segment + 1}: score ${r.replayedScore} vs ${r.recordedScore ?? '—'} recorded, ` +
        `end tick ${r.replayedFinalTick} vs ${r.recordedFinalTick ?? '—'} — ${r.note}`;
      this.verifyEl.appendChild(row);
    }
    this.sync();
  }
}
